import { decryptToken, encryptToken, isEncryptedValue } from "@/lib/tokenCrypto";

const ACCOUNTS_KEY = "mycoaching_saved_accounts";
export const MAX_SAVED_ACCOUNTS = 5;

let accountsCache = null;
let cacheReady = false;

function readStorageRaw() {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function persistEncrypted(accounts) {
  try {
    const stored = await Promise.all(
      accounts.map(async (a) => ({
        ...a,
        token: a.token ? await encryptToken(a.token) : a.token,
      }))
    );
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(stored));
  } catch {
    /* ignore */
  }
}

function writeAccounts(accounts) {
  accountsCache = accounts;
  cacheReady = true;
  persistEncrypted(accounts);
}

function sortAccounts(accounts) {
  return [...accounts].sort((a, b) => (b.last_used_at || 0) - (a.last_used_at || 0));
}

/** Initialise le cache (déchiffre les jetons). À appeler au boot auth. */
export async function initAccountsCrypto() {
  const raw = readStorageRaw();
  const decrypted = await Promise.all(
    raw.map(async (a) => ({
      ...a,
      token: a.token ? await decryptToken(a.token) : a.token,
    }))
  );

  const needsMigration = raw.some((a) => a.token && !isEncryptedValue(a.token));
  accountsCache = sortAccounts(decrypted);
  cacheReady = true;

  if (needsMigration) {
    await persistEncrypted(accountsCache);
  }

  return accountsCache;
}

function readAccountsRaw() {
  if (cacheReady && accountsCache) {
    return accountsCache;
  }
  return sortAccounts(readStorageRaw());
}

/** Liste publique sans jetons (affichage UI). */
export function getSavedAccounts() {
  return sortAccounts(readAccountsRaw()).map(({ token: _t, ...rest }) => rest);
}

export function getSavedAccountsWithTokens() {
  return sortAccounts(readAccountsRaw());
}

export function getSavedAccountEntry(userId) {
  return readAccountsRaw().find((a) => a.user_id === userId) || null;
}

export function upsertSavedAccount(token, user) {
  if (!token || !user?.user_id) return;

  const accounts = readAccountsRaw();
  const now = Date.now();
  const existing = accounts.find((a) => a.user_id === user.user_id);

  const entry = {
    user_id: user.user_id,
    email: user.email || existing?.email || "",
    name: user.name || existing?.name || "Compte",
    role: user.role || existing?.role || "student",
    picture: user.picture ?? existing?.picture ?? null,
    token,
    last_used_at: now,
    saved_at: existing?.saved_at || now,
  };

  const next = accounts.filter((a) => a.user_id !== user.user_id);
  next.unshift(entry);
  writeAccounts(sortAccounts(next).slice(0, MAX_SAVED_ACCOUNTS));
}

export function touchSavedAccount(userId) {
  const accounts = readAccountsRaw();
  const idx = accounts.findIndex((a) => a.user_id === userId);
  if (idx < 0) return;
  accounts[idx].last_used_at = Date.now();
  writeAccounts(sortAccounts(accounts));
}

export function updateSavedAccountToken(userId, token) {
  if (!userId || !token) return;
  const accounts = readAccountsRaw();
  const idx = accounts.findIndex((a) => a.user_id === userId);
  if (idx < 0) return;
  accounts[idx].token = token;
  accounts[idx].last_used_at = Date.now();
  writeAccounts(sortAccounts(accounts));
}

export function getSavedAccountsCount() {
  return readAccountsRaw().length;
}

export function removeSavedAccount(userId) {
  writeAccounts(readAccountsRaw().filter((a) => a.user_id !== userId));
}

export function migrateCurrentSessionToAccounts(token, user) {
  if (!token || !user?.user_id) return;
  upsertSavedAccount(token, user);
}

export function clearAllSavedAccounts() {
  accountsCache = [];
  cacheReady = true;
  try {
    localStorage.removeItem(ACCOUNTS_KEY);
  } catch {
    /* ignore */
  }
}